import { useEffect, useRef } from 'react';
import { navigate } from 'wouter/use-browser-location';

type LeaveAttempt = (proceed: () => void) => void;
const HISTORY_POSITION = '__managementHistoryPosition';
interface Blocker {
  attempt: LeaveAttempt;
  ignoreSamePathQuery: boolean;
}
const blockers = new Map<symbol, Blocker>();
let bypass = false;
let initialized = false;
let position = 0;
let restoring: (() => void) | null = null;
let acceptingPop = false;
let currentPath = typeof window === 'undefined' ? '' : window.location.pathname;

function once(action: () => void) {
  let performed = false;
  return () => {
    if (performed) return;
    performed = true;
    bypass = true;
    try {
      action();
    } finally {
      bypass = false;
    }
  };
}

/** The guard that answers a change: any guard for a path change, one that protects queries for a same-path change. */
function blockerFor(samePath: boolean) {
  const all = [...blockers.values()];
  return (samePath ? all.filter((b) => !b.ignoreSamePathQuery) : all).at(-1);
}

/** Track app history entries so a rejected Back restores the original entry, not a copy. */
function initializeHistoryGuard() {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;
  const push = window.history.pushState.bind(window.history);
  const replace = window.history.replaceState.bind(window.history);
  position = window.history.state?.[HISTORY_POSITION] ?? 0;
  const withPosition = (state: unknown, next: number) => ({
    ...(state && typeof state === 'object' ? state : {}),
    [HISTORY_POSITION]: next,
  });
  replace(withPosition(window.history.state, position), '', window.location.href);
  for (const [method, original] of [
    ['pushState', push],
    ['replaceState', replace],
  ] as const) {
    window.history[method] = (state, unused, url) => {
      const target = url == null ? window.location.href : new URL(url, window.location.href).href;
      const perform = () => {
        const next = method === 'pushState' ? position + 1 : position;
        original(withPosition(state, next), unused, url);
        position = next;
        currentPath = window.location.pathname;
      };
      const blocker = blockerFor(new URL(target).pathname === window.location.pathname);
      if (!bypass && blocker && target !== window.location.href) blocker.attempt(once(perform));
      else perform();
    };
  }
  window.addEventListener(
    'popstate',
    (event) => {
      const next = event.state?.[HISTORY_POSITION] as number | undefined;
      if (restoring) {
        event.stopImmediatePropagation();
        const restored = restoring;
        restoring = null;
        restored();
        return;
      }
      if (acceptingPop) {
        acceptingPop = false;
        position = next ?? position;
        currentPath = window.location.pathname;
        return;
      }
      const blocker = blockerFor(window.location.pathname === currentPath);
      if (!blocker) {
        position = next ?? position;
        currentPath = window.location.pathname;
        return;
      }
      // App entries are indexed from startup. A pre-app same-document entry is behind it.
      const delta = next === undefined ? -1 : next - position;
      if (delta === 0) return;
      event.stopImmediatePropagation();
      restoring = () =>
        blocker.attempt(
          once(() => {
            acceptingPop = true;
            window.history.go(delta);
          })
        );
      window.history.go(-delta);
    },
    true
  );
}

initializeHistoryGuard();

/**
 * While `active`, a click on an in-app link is held back and `onAttempt` receives the
 * navigation to run once the user agrees. Closing or reloading the tab gets the browser's
 * own prompt. Links that open elsewhere (new tab, download, another origin) pass through.
 * With ignoreSamePathQuery, history changes that keep the pathname are not held.
 */
export function useLeaveGuard(
  active: boolean,
  onAttempt: (proceed: () => void) => void,
  options: { ignoreSamePathQuery?: boolean } = {}
): void {
  const ignoreSamePathQuery = options.ignoreSamePathQuery ?? false;
  const onAttemptRef = useRef(onAttempt);
  useEffect(() => {
    onAttemptRef.current = onAttempt;
  });

  useEffect(() => {
    if (!active) return;
    const token = Symbol('leave-guard');
    blockers.set(token, {
      attempt: (proceed) => onAttemptRef.current(proceed),
      ignoreSamePathQuery,
    });

    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = (event.target as Element | null)?.closest?.('a[href]');
      if (!(anchor instanceof HTMLAnchorElement)) return;
      if (anchor.target && anchor.target !== '_self') return;
      if (anchor.hasAttribute('download')) return;
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      if (ignoreSamePathQuery && url.pathname === window.location.pathname) return;
      const next = url.pathname + url.search + url.hash;
      const current = window.location.pathname + window.location.search + window.location.hash;
      if (next === current) return;
      // Capture phase on the document runs before React's handlers, so the link never fires.
      event.preventDefault();
      event.stopImmediatePropagation();
      onAttemptRef.current(once(() => navigate(next)));
    };
    const onBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();

    document.addEventListener('click', onClick, true);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      blockers.delete(token);
      document.removeEventListener('click', onClick, true);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [active, ignoreSamePathQuery]);
}
